import JSZip from "jszip";
import { saveAs } from "file-saver";
import { supabase } from "../SupabaseClient.tsx";

// Attachment type for order_attachments
export type LibraryAttachment = {
  id: string;
  order_id: string;
  storage_path: string;
  original_filename: string;
  mime_type: string | null;
  is_primary: boolean;
};

export type LibraryOrder = {
  id: string;
  purchase_date: string;
  amount: number;
  status: { name: string };
  origin?: { name: string };
  name?: string;
  notes?: string;
  beneficiary?: string;
  merchant?: string;
  category?: string;
  purchase_id?: string;
  created_at?: string;
  attachments?: LibraryAttachment[];
};

// Fetch every receipt with its attachments. Returns null if the receipts couldn't be loaded.
export async function fetchLibraryOrders(): Promise<LibraryOrder[] | null> {
  // First fetch orders
  const { data, error } = await supabase
    .from("orders")
    .select(
      "id, purchase_date, amount, status(name), origin(name), name, notes, beneficiary, merchant, category, purchase_id, created_at"
    )
    .order("created_at", { ascending: false });

  if (error || !data) {
    console.error("Error fetching orders:", error);
    return null;
  }

  // Fetch all attachments for these orders
  const orderIds = data.map((o: any) => o.id);
  const { data: attachmentsData, error: attachmentsError } = await supabase
    .from("order_attachments")
    .select(
      "id, order_id, storage_path, original_filename, mime_type, is_primary"
    )
    .in("order_id", orderIds);

  if (attachmentsError) {
    console.error("Error fetching attachments:", attachmentsError);
  }

  // Group attachments by order_id
  const attachmentsByOrderId: Record<string, LibraryAttachment[]> = {};
  (attachmentsData || []).forEach((attachment: LibraryAttachment) => {
    if (!attachmentsByOrderId[attachment.order_id]) {
      attachmentsByOrderId[attachment.order_id] = [];
    }
    attachmentsByOrderId[attachment.order_id].push(attachment);
  });

  // Map orders with their attachments
  return data.map((order: any) => ({
    ...order,
    status: Array.isArray(order.status) ? order.status[0] : order.status,
    origin: Array.isArray(order.origin) ? order.origin[0] : order.origin,
    attachments: attachmentsByOrderId[order.id] || [],
  }));
}

// Helper to escape a value for CSV (handles commas, quotes, newlines)
const escapeCsvValue = (value: string): string => {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

// Download every receipt as a zip of their files and CSVs of their details. Someone whose
// access has ended relies on this for their records, so returns a message to show them if
// anything is missing from the download, or null if it's complete.
export async function exportReceipts(
  orders: LibraryOrder[]
): Promise<string | null> {
  const zip = new JSZip();

  // Track used filenames to handle duplicates for single-attachment orders
  const usedFilenames = new Map<string, number>();

  // Helper function to get a unique filename
  const getUniqueFilename = (filename: string): string => {
    const count = usedFilenames.get(filename) || 0;
    usedFilenames.set(filename, count + 1);

    if (count === 0) {
      return filename;
    }

    // Split filename into name and extension
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return `${filename}_${count}`;
    }

    const name = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex);
    return `${name}_${count}${extension}`;
  };

  try {
    // Fetch all reimbursement data for these orders
    const orderIds = orders.map((o) => o.id);
    const { data: reimbursementsData } = await supabase
      .from("order_reimburse")
      .select("order_id, reimburse_date, reimburse_amount")
      .in("order_id", orderIds);

    // Group reimbursements by order_id
    const reimbursementsByOrderId: Record<
      string,
      { reimburse_date: string; reimburse_amount: number }[]
    > = {};
    let totalReimbursedByOrderId: Record<string, number> = {};
    (reimbursementsData || []).forEach((r: any) => {
      if (!reimbursementsByOrderId[r.order_id]) {
        reimbursementsByOrderId[r.order_id] = [];
      }
      reimbursementsByOrderId[r.order_id].push(r);
      totalReimbursedByOrderId[r.order_id] =
        (totalReimbursedByOrderId[r.order_id] || 0) + r.reimburse_amount;
    });

    // Track used folder names separately to avoid collisions with filenames
    const usedFolderNames = new Map<string, number>();
    const getUniqueFolderName = (name: string): string => {
      const count = usedFolderNames.get(name) || 0;
      usedFolderNames.set(name, count + 1);
      return count === 0 ? name : `${name}_${count}`;
    };

    // Track metadata rows for the CSV
    const csvRows: string[][] = [];
    const addCsvRow = (filePath: string, order: LibraryOrder) => {
      const reimbursedTotal = totalReimbursedByOrderId[order.id] || 0;
      csvRows.push([
        filePath,
        order.purchase_id || "",
        order.purchase_date || "",
        order.name || "",
        order.merchant || "",
        order.category || "",
        order.beneficiary || "",
        order.amount != null ? String(order.amount) : "",
        reimbursedTotal > 0 ? reimbursedTotal.toFixed(2) : "",
        order.status?.name || "",
        order.origin?.name || "",
        order.notes || "",
        order.created_at || "",
      ]);
    };

    let failedFiles = 0;

    // Fetch each file from Supabase storage and add to zip
    for (const order of orders) {
      if (order.attachments && order.attachments.length > 0) {
        const isMultiAttachment = order.attachments.length > 1;
        // Sanitize order name for use as a folder name
        const folderName = (order.name || order.id)
          .replace(/[/\\:*?"<>|]/g, "_")
          .trim();
        const uniqueFolder = isMultiAttachment
          ? getUniqueFolderName(folderName)
          : folderName;

        for (const attachment of order.attachments) {
          const { data, error } = await supabase.storage
            .from("order_receipts")
            .download(attachment.storage_path);

          if (!error && data) {
            const fileBlob = await data.arrayBuffer();

            let filePath: string;
            if (isMultiAttachment) {
              // Multi-attachment: place in a named subfolder under receipts/
              filePath = `receipts/${uniqueFolder}/${attachment.original_filename}`;
            } else {
              // Single-attachment: place flat under receipts/
              filePath = `receipts/${getUniqueFilename(attachment.original_filename)}`;
            }

            zip.file(filePath, fileBlob);

            // Track metadata for this file
            addCsvRow(filePath, order);
          } else {
            console.error("Error downloading attachment:", error);
            failedFiles += 1;
            addCsvRow("", order);
          }
        }
      } else {
        // Keep the details of a receipt that has no file
        addCsvRow("", order);
      }
    }

    // Build receipts metadata CSV and add to zip
    const csvHeader = [
      "file_path",
      "purchase_id",
      "purchase_date",
      "name",
      "merchant",
      "category",
      "beneficiary",
      "amount",
      "amount_reimbursed",
      "status",
      "origin",
      "notes",
      "created_at",
    ];
    const csvContent = [
      csvHeader.join(","),
      ...csvRows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");
    zip.file("receipts_metadata.csv", csvContent);

    // Build reimbursement metadata CSV and add to zip
    const reimbursementHeader = [
      "order_id",
      "purchase_id",
      "order_name",
      "reimburse_date",
      "reimburse_amount",
    ];
    const reimbursementRows: string[][] = [];
    for (const order of orders) {
      const orderReimbursements = reimbursementsByOrderId[order.id];
      if (orderReimbursements) {
        for (const r of orderReimbursements) {
          reimbursementRows.push([
            order.id,
            order.purchase_id || "",
            order.name || "",
            r.reimburse_date,
            r.reimburse_amount.toFixed(2),
          ]);
        }
      }
    }
    if (reimbursementRows.length > 0) {
      const reimbursementCsvContent = [
        reimbursementHeader.join(","),
        ...reimbursementRows.map((row) => row.map(escapeCsvValue).join(",")),
      ].join("\n");
      zip.file("reimbursement_metadata.csv", reimbursementCsvContent);
    }

    // Generate zip and trigger download
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "receipts.zip");

    return failedFiles > 0
      ? `${failedFiles} ${failedFiles === 1 ? "file" : "files"} couldn't be included in your download. Please try again, or contact support@shoebox.io.`
      : null;
  } catch (error) {
    console.error("Error exporting receipts:", error);
    return "We couldn't create your download. Please try again, or contact support@shoebox.io.";
  }
}
